/**
 * The Price tab: order-level money only. A summary of what the customer was
 * quoted and what the estimate reports, then one ▸ per build-up. The line's
 * own price and spec live on Lines.
 */
import { useMemo, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { EstimateDetailViewItem } from "@shared-core/data/api-types";
import type { MerchandiseDraftEstimate } from "@shared-core/data/merchandise-types";
import type {
  PrecedentCandidate,
  PricePrecedentView,
} from "@shared-core/data/price-precedent-types";
import {
  contributionPercent,
  estimateCarriageDetail,
  quotedTotalSourceLabel,
  tharsternCostLadder,
} from "@shared-core/domain/estimate-presentation";
import {
  formatCurrencyGbp,
  formatQuotedPrice,
} from "@shared-core/domain/formatters";
import { InfoTip } from "../../app/components/patterns/InfoTip";
import { PricePrecedentTable } from "../../app/components/domain/pricing/PricePrecedentPanel";
import { DraftEstimatePriceBuildUp } from "../../app/components/domain/merchandise/DraftEstimatePriceBuildUp";
import {
  Disclosure,
  Gap,
  KeyValues,
  MoneyLadder,
  Section,
  SubHeading,
  TabContainer,
} from "./atoms";

const MONEY = "tabular-nums";

/** A draft's Price: its basis, then its own build-up (buy → margin → quoted price). */
export function DraftPriceTab({ draft }: { draft: MerchandiseDraftEstimate }) {
  return (
    <TabContainer label="Financial Details">
      <Section title="Price basis">
        <KeyValues
          rows={[
            {
              label: "Quoted price (ex VAT)",
              value:
                draft.pricing?.ourPricePence != null
                  ? formatQuotedPrice(draft.pricing.ourPricePence)
                  : null,
              gap: "No price yet — awaiting quote",
            },
            {
              label: "Price basis",
              value: draft.pricing
                ? "The merchandise team's Promo Office price, taken as is"
                : "Priced once the merchandise team has quoted",
            },
          ]}
        />
      </Section>
      <Section title="Build-up">
        <DraftEstimatePriceBuildUp request={draft} />
      </Section>
    </TabContainer>
  );
}

/**
 * A non-draft's Price: what the customer was quoted, the reporting value,
 * then carriage, price precedent and Tharstern's cost build-up, one ▸ each.
 */
export function PriceTab({ estimate }: { estimate: EstimateDetailViewItem }) {
  const { financials } = estimate;
  const margin = contributionPercent(
    estimate.estimate_amt,
    estimate.contribution_amt,
  );
  const unitPence = financials.quoted_total_pence;
  return (
    <TabContainer label="Financial Details">
      <Section title="Summary">
        <div className="grid gap-6 md:grid-cols-2">
          <section aria-label="Quoted to the customer">
            <KeyValues
              rows={[
                {
                  label: "Quoted to the customer",
                  value:
                    financials.quoted_total_pence == null ? null : (
                      <span className={MONEY}>
                        {formatQuotedPrice(financials.quoted_total_pence)}
                      </span>
                    ),
                  gap: "No quoted total recorded",
                  info: quotedTotalSourceLabel(financials.quoted_total_source),
                },
              ]}
            />
          </section>
          <section aria-label="Reporting value">
            <KeyValues
              rows={[
                {
                  label: "Estimate value",
                  value: (
                    <span className={MONEY}>
                      {formatCurrencyGbp(estimate.estimate_amt, 2)}
                    </span>
                  ),
                },
                {
                  label: "Contribution",
                  value: (
                    <span className={MONEY}>
                      {formatCurrencyGbp(estimate.contribution_amt, 2)}
                    </span>
                  ),
                },
                {
                  label: "Margin",
                  // Zero-guarded in shared-core: tens of thousands of live
                  // rows carry a £0 estimate value, and dividing by it
                  // rendered "NaN%" / "-Infinity%" on this very line.
                  value:
                    margin == null ? null : (
                      <span className={MONEY}>{`${margin.toFixed(1)}%`}</span>
                    ),
                  gap: "Not calculable",
                  info:
                    margin == null
                      ? "The estimate's reporting value is £0.00, so there is nothing to take a margin against. On a freshly-sent estimate this normally means the reporting figures have not synced yet."
                      : null,
                },
              ]}
            />
          </section>
        </div>
      </Section>

      <Section title="Build-ups" label="Build-ups">
        <Disclosure title="Carriage build-up" id="price-carriage">
          <CarriageBuildUp financials={financials} />
        </Disclosure>
        <Disclosure title="Price precedent" id="price-precedent">
          {/* The customer's own history for this spec: "was this the right
              number" answered next to the number (price-precedent brief J3).
              Coverage is explicit — a first order and an unavailable lookup
              render as sentences, never as an empty table. */}
          <EstimatePrecedent
            key={estimate.id}
            estimateId={estimate.id}
            precedent={financials.precedent}
            quotedUnitPence={
              unitPence != null && estimate.quantity > 0
                ? unitPence / estimate.quantity
                : null
            }
          />
        </Disclosure>
        <Disclosure title="Tharstern cost build-up" id="price-tharstern">
          <TharsternBuildUp financials={financials} />
        </Disclosure>
      </Section>
    </TabContainer>
  );
}

// ── The build-ups ──────────────────────────────────────────────────────────

function TharsternBuildUp({
  financials,
}: {
  financials: EstimateDetailViewItem["financials"];
}) {
  const ladder = tharsternCostLadder(financials.tharstern);
  return (
    <section aria-label="Tharstern cost build-up" className="space-y-3">
      <SubHeading
        aside={<span className="text-xs text-muted-foreground">Tharstern</span>}
      >
        Manufacturing cost to price
      </SubHeading>
      {ladder.kind === "unavailable" ? (
        <p className="text-sm">
          <Gap>No cost build-up</Gap>{" "}
          <InfoTip label="Why there is no cost build-up">
            {ladder.explanation}
          </InfoTip>
        </p>
      ) : (
        <>
          {ladder.kind === "totals_only" && (
            <p className="text-sm">
              <Gap>Totals only</Gap>{" "}
              <InfoTip label="Why only totals">{ladder.explanation}</InfoTip>
            </p>
          )}
          {ladder.rows.length > 0 && <MoneyLadder rows={ladder.rows} />}
          {ladder.asides.length > 0 && (
            <div className="pt-3 border-t border-border">
              <div className="text-xs text-muted-foreground mb-1">
                Also reported by Tharstern
              </div>
              <MoneyLadder rows={ladder.asides} />
            </div>
          )}
        </>
      )}
    </section>
  );
}

function CarriageBuildUp({
  financials,
}: {
  financials: EstimateDetailViewItem["financials"];
}) {
  const carriage = estimateCarriageDetail(financials.carriage);
  return (
    <section aria-label="Carriage" className="space-y-2">
      {carriage.kind === "not_captured" ? (
        <p className="text-sm">
          <Gap>No carriage figure recorded</Gap>{" "}
          <InfoTip label="Why there is no carriage figure">
            {carriage.explanation}
          </InfoTip>
        </p>
      ) : (
        <>
          {carriage.kind === "cost_only" && (
            <p className="text-sm text-muted-foreground">
              Cost only{" "}
              <InfoTip label="About the carriage cost">
                {carriage.explanation}
              </InfoTip>
            </p>
          )}
          <MoneyLadder rows={carriage.rows} />
          {carriage.isManual === true && (
            <p className="text-sm text-muted-foreground">
              Entered manually{" "}
              <InfoTip label="About manual carriage">
                Carriage was entered manually on this estimate rather than
                priced by the rules.
              </InfoTip>
            </p>
          )}
        </>
      )}
    </section>
  );
}

/**
 * The customer's history for this estimate's spec, with search and link when
 * nothing was recognised — the common case today, since most quotes can be
 * matched on their product code only. A link is recorded against the
 * precedent's own `spec_identity` and the block is re-read from
 * `/views/price-precedent`, so the page never guesses what the link changed.
 */
function EstimatePrecedent({
  estimateId,
  precedent: initial,
  quotedUnitPence,
}: {
  estimateId: string;
  precedent: PricePrecedentView;
  quotedUnitPence: number | null;
}) {
  const adapter = useDataAdapter();
  const [precedent, setPrecedent] = useState(initial);
  const [linkError, setLinkError] = useState<string | null>(null);
  const customerCode = precedent.customer_code;
  const specIdentity = precedent.spec_identity;
  const onSearchCandidates = useMemo(
    () =>
      customerCode
        ? (query: string) =>
            adapter.searchPrecedentCandidates(customerCode, query)
        : undefined,
    [adapter, customerCode],
  );
  const onLinkPrecedent =
    customerCode && specIdentity
      ? (candidate: PrecedentCandidate) => {
          setLinkError(null);
          adapter
            .linkPrecedent({ customerCode, specIdentity, candidate })
            .then(() => adapter.getEstimatePricePrecedent(estimateId))
            .then(setPrecedent)
            .catch(() =>
              setLinkError(
                `Couldn't link ${candidate.reference} — try again, or check it belongs to this customer.`,
              ),
            );
        }
      : undefined;
  return (
    <PricePrecedentTable
      precedent={precedent}
      quotedUnitPence={quotedUnitPence}
      onSearchCandidates={onSearchCandidates}
      onLinkPrecedent={onLinkPrecedent}
      linkError={linkError}
    />
  );
}
